import { Box, Typography, Card, CardContent, Grid, Chip } from '@mui/material';
import PageGuide from "../../components/PageGuide";
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const reportCards = [
  {
    title: 'Piece Trace Report',
    desc: 'Full traceability chain: Heat → CMTR → Release Package → BOL',
    path: '/reports/piece-trace',
    status: 'live',
  },
  {
    title: 'Assembly Heat Report',
    desc: 'All heats contributing to an assembly — for MTR submittals',
    path: '/reports/assembly-heat',
    status: 'live',
  },
  {
    title: 'Heat Ledger Report',
    desc: 'Receipt, issue, and consumed quantities per heat number',
    path: '/reports/heat-ledger',
    status: 'live',
  },
  {
    title: 'NCR / CAPA Summary',
    desc: 'Open and closed NCRs with corrective action status',
    path: '/reports/ncr-summary',
    status: 'live',
  },
  {
    title: 'Audit Log',
    desc: 'Filter and export full audit trail by user, module, action, and date range',
    path: '/reports/audit-log',
    status: 'live',
  },
  {
    title: 'Shipping Load Summary',
    desc: 'Load IDs, piece counts, BOL dates, and piece marks by job',
    path: '/reports/shipping-load',
    status: 'live',
  },
];

export default function Reports() {
  const navigate = useNavigate();

  const handleClick = (r) => {
    if (r.path) {
      navigate(r.path);
    } else {
      toast.info(`${r.title} is coming soon.`, { position: 'top-right', autoClose: 3000 });
    }
  };

  return (
    <>
      <Helmet>
        <title>Reports | SFM</title>
      </Helmet>
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>Reports</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select a report to view traceability, quality, and shipping data for your jobs.
        </Typography>
        <PageGuide
          why="Pull a job's records together — traceability, quality, and shipping — without digging through each module."
          who="The QM and PMs, pulling a job's records for review or audit."
          what="A set of reports across heats, pieces, NCRs, shipping, and the audit log."
          how="Pick a report, filter by job, and export to CSV or print."
          when="For progress reviews, customer reporting, and audit preparation."
          standard="ISO 9001:2015 §9.1"
        />
        <Grid container spacing={2}>
          {reportCards.map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.title}>
              <Card
                onClick={() => handleClick(r)}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  opacity: r.status === 'soon' ? 0.7 : 1,
                  '&:hover': {
                    borderColor: r.status === 'live' ? '#007AFF' : '#aaa',
                    boxShadow: r.status === 'live' ? '0 2px 8px rgba(0,122,255,0.18)' : 'none',
                  },
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontSize: 16 }}>
                      {r.title}
                    </Typography>
                    {r.status === 'live' ? (
                      <Chip label="Live" size="small" color="success" />
                    ) : (
                      <Chip label="Soon" size="small" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {r.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}
